"use client";

import { PlusIcon } from "@heroicons/react/20/solid";
import {
  ComponentPropsWithoutRef,
  FC,
  ReactElement,
  cloneElement,
  useState,
} from "react";
import { ReactSVG } from "react-svg";
import { REVIEW_USER_EMAILS } from "../const/webConst";
import { useConnectAccountPoll } from "../hooks/useConnectAccountPoll";
import { SocialProviderType } from "../types/models";
import Button from "./Button";
import { COMING_SOON_PROVIDERS } from "./NotConnectedSocialProviderAccountAvatar";
import DropdownMenu from "./DropdownMenu";
import { InputOption } from "./SelectInput";
import { useUser } from "./SupabaseProvider";

export const providerTypeToIconLabel: Record<
  SocialProviderType,
  { icon: ReactElement<HTMLDivElement>; label: string }
> = {
  tiktok: {
    icon: <ReactSVG src="/svg/TikTok_logomark.svg" />,
    label: "TikTok",
  },
  youtube: {
    icon: <ReactSVG src="/svg/Shorts_logo.svg" className="fill-red-500" />,
    label: "YouTube",
  },
  linkedin: {
    icon: <ReactSVG src="/svg/Linkedin_logomark.svg" />,
    label: "LinkedIn",
  },

  instagram: {
    icon: <ReactSVG src="/svg/Instagram_logomark.svg" />,
    label: "Instagram",
  },
  facebook: {
    icon: <ReactSVG src="/svg/Facebook_logomark.svg" />,
    label: "Facebook",
  },
};

const ProviderButton = ({ provider }: { provider: SocialProviderType }) => {
  const { connect } = useConnectAccountPoll();
  const user = useUser();
  const isReview = REVIEW_USER_EMAILS.includes(user?.email ?? "");
  const comingSoonProviders = isReview ? [] : COMING_SOON_PROVIDERS;

  return (
    <Button
      disabled={comingSoonProviders.includes(provider)}
      data-tooltip-content={
        comingSoonProviders.includes(provider) ? "Coming soon" : undefined
      }
      data-tooltip-id="global"
      size="m"
      className="w-full hover:!bg-slate-100"
      variant="tertiary"
      align="start"
      onClick={() => connect(provider)}
    >
      <Button.Content>
        {isReview ? (
          <div className="bg-white border border-slate-200 p-1 rounded-lg -ml-3">
            {cloneElement(providerTypeToIconLabel[provider].icon, {
              className: `${providerTypeToIconLabel[provider].icon.props.className} w-5 h-5`,
            })}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 p-1.5 rounded-lg -ml-3">
            {cloneElement(providerTypeToIconLabel[provider].icon, {
              className: `${providerTypeToIconLabel[provider].icon.props.className} w-4 h-4`,
            })}
          </div>
        )}
      </Button.Content>
      {providerTypeToIconLabel[provider].label}
    </Button>
  );
};

type AddSocialProviderAccountButtonProps = {} & ComponentPropsWithoutRef<"div">;

export const providerOptions: InputOption<SocialProviderType>[] = [
  {
    label: "TikTok",
    value: "tiktok",
  },
  {
    label: "YouTube",
    value: "youtube",
  },
  {
    label: "Instagram",
    value: "instagram",
  },
  {
    label: "LinkedIn",
    value: "linkedin",
  },
  {
    label: "Facebook",
    value: "facebook",
  },
];

const AddSocialProviderAccountButton: FC<
  AddSocialProviderAccountButtonProps
> = ({ ...rest }) => {
  const [showProviders, setShowProviders] = useState(false);
  const user = useUser();
  const isReview = REVIEW_USER_EMAILS.includes(user?.email ?? "");

  if (isReview)
    return (
      <DropdownMenu
        positions={["bottom"]}
        containerClassName="w-48"
        open={showProviders}
        onOpenChange={setShowProviders}
      >
        <DropdownMenu.Trigger>
          <button
            data-tooltip-content={"Add account"}
            data-tooltip-id="global"
            className={`${rest.className} w-10 h-10 border border-dashed border-slate-200 flex items-center justify-center rounded-full`}
          >
            <PlusIcon className={"w-4 h-4 text-slate-500"} />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          <ul>
            {providerOptions.map((option) => (
              <li key={option.value}>
                <ProviderButton provider={option.value} />
              </li>
            ))}
          </ul>
        </DropdownMenu.Content>
      </DropdownMenu>
    );
  return (
    <DropdownMenu
      positions={["bottom"]}
      containerClassName="w-48"
      open={showProviders}
      onOpenChange={setShowProviders}
    >
      <DropdownMenu.Trigger>
        <button
          data-tooltip-content={"Add account"}
          data-tooltip-id="global"
          className={`${rest.className} w-10 h-10 border border-dashed border-slate-200 flex items-center justify-center rounded-full`}
        >
          <PlusIcon className={"w-4 h-4 text-slate-500"} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <ul>
          {providerOptions.map((option) => (
            <li key={option.value}>
              <ProviderButton provider={option.value} />
            </li>
          ))}
        </ul>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default AddSocialProviderAccountButton;
